<script lang="ts" setup>
  import { computed } from "vue";

  import { DateFormat, formatDate } from "shared/utils/date-utils";
  import { PrimaryKey } from "frontend/interfaces/primary-key";
  import { Appointment } from "frontend/interfaces/p/appointment";
  import { appointmentStatus } from "frontend/utils/appointment-list-utils";

  import BaseBox from "frontend/components/base/BaseBox.vue";
  import BaseButton from "frontend/components/base/BaseButton.vue";

  interface LocalAppointment {
    id: PrimaryKey;
    name: string;
    begin: Date;
    status: number;
    persons: string[];
  }

  type PossibleAppointment = LocalAppointment | Appointment;

  const localAppointmentDiscriminator = (
    element: PossibleAppointment
  ): element is LocalAppointment => !(element as Appointment)._policy;
  const appointmentDiscriminator = (
    element: PossibleAppointment
  ): element is Appointment => !localAppointmentDiscriminator(element);

  const props = withDefaults(
    defineProps<{
      appointments?: PossibleAppointment[];
      title?: string | null;
      note?: string | null;
    }>(),
    { appointments: () => [], title: null, note: null }
  );

  defineEmits<{
    (e: "deleteAppointment", appointment: Appointment): void;
  }>();

  const theLocalAppointments = computed<LocalAppointment[]>(() =>
    props.appointments.filter(localAppointmentDiscriminator)
  );
  const theAppointments = computed<Appointment[]>(() =>
    props.appointments.filter(appointmentDiscriminator)
  );
</script>

<template>
  <div>
    <h5>{{ title }}</h5>
    <div v-if="appointments.length > 0">
      <div v-if="theLocalAppointments.length > 0">
        <table class="appointment-table">
          <thead>
            <tr>
              <th></th>
              <th>Termintyp</th>
              <th>Datum</th>
              <th>Uhrzeit</th>
              <th>Mitarbeiter</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="appointment in theLocalAppointments"
              v-bind:key="appointment.id"
            >
              <td>
                <RouterLink
                  v-bind:to="{
                    name: 'calendar-day',
                    params: {
                      day: formatDate(
                        appointment.begin,
                        DateFormat.DateOnlyISO
                      ),
                    },
                    query: {
                      highlightAppointmentId: appointment.id,
                    },
                  }"
                >
                  <i
                    title="im Kalender anzeigen"
                    class="fe fe-calendar-day"
                  ></i>
                </RouterLink>
              </td>
              <td>{{ appointment.name }}</td>
              <td>
                {{ formatDate(appointment.begin, DateFormat.DateOnly) }}
              </td>
              <td>
                {{
                  formatDate(
                    appointment.begin,
                    DateFormat.TimeOnlyWithoutSeconds
                  )
                }}
              </td>
              <td>{{ appointment.persons.join(", ") }}</td>
              <td>
                {{ appointmentStatus(appointment.status) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="theAppointments.length > 0">
        <BaseBox
          v-for="appointment in theAppointments"
          v-bind:key="appointment.id"
          class="appointment-box"
          visual-no-heading
        >
          <div class="appointment-box__container">
            <i class="fe fe-calendar-day appointment-icon__calendar"></i>
            <div>
              <div class="appointment__date">
                <strong>
                  {{ formatDate(appointment.begin, DateFormat.DateOnlyLong) }}
                  um
                  {{
                    formatDate(
                      appointment.begin,
                      DateFormat.TimeOnlyWithoutSeconds
                    )
                  }}
                </strong>
              </div>
              <div>Besuchsgrund: {{ appointment.appointment_type_name }}</div>
            </div>
            <BaseButton
              v-if="appointment._policy.destroy"
              class="appointment-button__delete"
              visual-icon
              v-on:submit="$emit('deleteAppointment', appointment)"
              ><i class="fe fe-trash"
            /></BaseButton>
          </div>
        </BaseBox>
      </div>
    </div>
    <div v-else>{{ note }}</div>
  </div>
</template>

<style lang="scss" scoped>
  @use "shared/styles/colors";

  .appointment-box {
    margin: 10px 0;
  }

  .appointment-box__container {
    display: flex;
  }

  .appointment-icon__calendar {
    font-size: 20px;
    padding-right: 10px;
  }

  .appointment__date {
    margin-bottom: 5px;
  }

  .appointment-button__delete {
    background-color: transparent;
    color: colors.$color_main-area--background;
    font-size: 20px;
    padding: 0;
    margin-left: auto;
    align-self: center;

    &:hover {
      background-color: transparent;
      font-size: 30px;
    }
  }

  .appointment__delete-reason {
    color: colors.$color_base-list__light--text;
  }

  .appointment-table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    border: 1px solid colors.$color_base-list--separator;

    th,
    td {
      padding: 8px 10px;
      text-align: left;
      border-bottom: 1px solid colors.$color_base-list--separator;
    }

    th {
      font-weight: normal;
      background-color: colors.$color_patient-search__patient--background;
    }

    tr:last-child td {
      border-bottom: none;
    }
  }
</style>
